import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SwitchButton from "../../../components/SwitchButton/SwitchButton";
import AddPhoto from "./../../../assets/add-photo.png";
import Delete from "./../../../assets/delete.svg";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import api from "../../../utils/api";

// Main Variations Component
const Variations = ({
  isMobile,
  onVariationsChange,
  controlGroupHasDiscount,
  rangesOnly,
  onVariationsActiveChange,
  onVariationsHaveDiscountChange,
}) => {
  const [checked, setChecked] = useState(false);
  const [featureGroups, setFeatureGroups] = useState([{ id: 0 }]);
  const [featuresData, setFeaturesData] = useState({});
  const [globalFeatureType, setGlobalFeatureType] = useState("");
  const [globalLoading, setGlobalLoading] = useState(false);
  const [groupDiscounts, setGroupDiscounts] = useState({});

  const handleGlobalFeatureTypeChange = (event) => {
    setGlobalFeatureType(event.target.value);
  };

  const handleLoadingChange = useCallback((val) => setGlobalLoading(val), []);

  const handleAddGroup = () => {
    setFeatureGroups((prev) => [...prev, { id: prev.length }]);
  };

  const handleDeleteGroup = (groupId) => {
    // Remove the specified group
    const updatedGroups = featureGroups.filter((group) => group.id !== groupId);

    // Update featuresData to reflect the change - remove the deleted group
    setFeaturesData((prev) => {
      const newData = { ...prev };
      delete newData[groupId];
      return newData;
    });

    setGroupDiscounts((prev) => {
      const newData = { ...prev };
      delete newData[groupId];
      return newData;
    });

    // Set the updated groups
    setFeatureGroups(updatedGroups);
  };

  const handleGroupDiscountChange = useCallback((groupId, hasDiscount) => {
    setGroupDiscounts((prev) => ({ ...prev, [groupId]: hasDiscount }));
  }, []);

  useEffect(() => {
    if (onVariationsHaveDiscountChange) {
      const anyHasDiscount = Object.values(groupDiscounts).some(Boolean);
      onVariationsHaveDiscountChange(anyHasDiscount);
    }
  }, [groupDiscounts, onVariationsHaveDiscountChange]);

  // Handle feature data change from each FeatureGroup component
  const handleFeatureDataChange = useCallback(
    (formData, groupId, arrayIndex) => {
      setFeaturesData((prev) => ({
        ...prev,
        [groupId]: { formData, arrayIndex },
      }));
    },
    [],
  );

  // When featuresData changes or checked state changes, send combined data to parent
  useEffect(() => {
    if (onVariationsChange) {
      // Only send data if features are enabled (checked is true)
      if (!checked) {
        const emptyFormData = new FormData();
        onVariationsChange(emptyFormData);
        return;
      }

      // Combine all feature group data
      const combinedFormData = new FormData();

      // Add variation_type for each group
      Object.entries(featuresData).forEach(
        ([groupId, { formData, arrayIndex }]) => {
          if (formData) {
            // Get variation type from the form data
            const variationType = formData.get("variation_type");
            // Set variation_type with index
            combinedFormData.append(
              `variation_type[${arrayIndex}]`,
              variationType,
            );

            // Handle size_id, color_id, unit_id based on variation type
            if (variationType.includes("size")) {
              const sizeId = formData.get("size_id");
              if (sizeId) {
                combinedFormData.append(
                  `variations[${arrayIndex}][size_id]`,
                  sizeId,
                );
              }
            }

            if (variationType.includes("color")) {
              const colorId = formData.get("color_id");
              if (colorId) {
                combinedFormData.append(
                  `variations[${arrayIndex}][color_id]`,
                  colorId,
                );
              }
            }

            if (variationType.includes("unit")) {
              const unitId = formData.get("unit_id");
              if (unitId) {
                combinedFormData.append(
                  `variations[${arrayIndex}][unit_id]`,
                  unitId,
                );
              }
            }

            // Handle price and quantity
            const price = formData.get("variation_price");
            const qty = formData.get("variation_qty");

            if (price) {
              combinedFormData.append(
                `variations[${arrayIndex}][price]`,
                price,
              );
            }

            if (qty) {
              combinedFormData.append(`variations[${arrayIndex}][qty]`, qty);
            }

            // Handle images
            const imagesList = formData.getAll("variation_images");
            imagesList.forEach((image, imgIndex) => {
              combinedFormData.append(
                `variations[${arrayIndex}][variations_images][${imgIndex}]`,
                image,
              );
            });

            // Handle discount info
            const discount = formData.get("variation_discount");
            const maxDiscount = formData.get("variation_max_discount");
            const discountDateFrom = formData.get(
              "variation_discount_date_from",
            );
            const discountDateTo = formData.get("variation_discount_date_to");

            if (discount) {
              combinedFormData.append(
                `variations[${arrayIndex}][percentage_discount]`,
                discount,
              );
            }

            if (maxDiscount) {
              combinedFormData.append(
                `variations[${arrayIndex}][fixed_discount]`,
                maxDiscount,
              );
            }

            // Always include discount dates even if empty
            combinedFormData.append(
              `variations[${arrayIndex}][discount_date_from]`,
              discountDateFrom || "",
            );
            combinedFormData.append(
              `variations[${arrayIndex}][discount_date_to]`,
              discountDateTo || "",
            );

            // Handle price ranges if they exist
            // Get the hasScope value from the form data
            const hasScope = formData.get("has_scope") === "true";

            if (hasScope) {
              // Extract all price range data
              const minQtyList = [];
              const maxQtyList = [];
              const priceRangeList = [];

              // Get all price ranges from formData
              for (let [key, value] of formData.entries()) {
                if (key.includes("attribute_min_qty")) {
                  minQtyList.push(value);
                } else if (key.includes("attribute_max_qty")) {
                  maxQtyList.push(value);
                } else if (key.includes("attribute_price_range")) {
                  priceRangeList.push(value);
                }
              }

              // Process price ranges
              if (minQtyList.length > 0) {
                for (let i = 0; i < minQtyList.length; i++) {
                  combinedFormData.append(
                    `variations[${arrayIndex}][prices][${i}][min_qty]`,
                    minQtyList[i],
                  );
                  combinedFormData.append(
                    `variations[${arrayIndex}][prices][${i}][max_qty]`,
                    maxQtyList[i],
                  );
                  combinedFormData.append(
                    `variations[${arrayIndex}][prices][${i}][price]`,
                    priceRangeList[i],
                  );
                }
              }
            }
          }
        },
      );

      onVariationsChange(combinedFormData);
    }
  }, [featuresData, checked, onVariationsChange]);

  // Also handle the checked state change
  useEffect(() => {
    if (onVariationsChange && !checked) {
      const emptyFormData = new FormData();
      onVariationsChange(emptyFormData);
    }
  }, [checked, onVariationsChange]);

  // Reset checked when rangesOnly becomes true
  useEffect(() => {
    if (rangesOnly && checked) {
      setChecked(false);
      if (onVariationsActiveChange) onVariationsActiveChange(false);
    }
  }, [rangesOnly, checked, onVariationsActiveChange]);

  return (
    <div className="variations">
      <div
        className="variations-header"
        style={{
          marginBottom: "16px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <h4>السمات</h4>

        <div className="" style={{ display: "flex", alignItems: "center" }}>
          {rangesOnly && (
            <span
              style={{ fontSize: "12px", color: "#888", marginLeft: "8px" }}
            >
              (معطل - تم تفعيل نطاقات الأسعار)
            </span>
          )}
          <SwitchButton
            checked={checked && !rangesOnly}
            onChange={() => {
              if (rangesOnly) return;
              const next = !checked;
              setChecked(next);
              if (onVariationsActiveChange) onVariationsActiveChange(next);
            }}
            disabled={rangesOnly}
          />
        </div>
      </div>

      {checked && !rangesOnly && (
        <>
          {/* Global variation type selector — applies to all variations */}
          <FormControl
            sx={{ width: isMobile ? "100%" : "auto", minWidth: 220, mb: 2 }}
          >
            <InputLabel
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              نوع السمة
            </InputLabel>
            <Select
              displayEmpty
              sx={{ borderRadius: "8px" }}
              value={globalFeatureType}
              disabled={globalLoading}
              onChange={handleGlobalFeatureTypeChange}
            >
              <MenuItem value="">
                {globalLoading ? "جاري التحميل ..." : "اختر السمة"}
              </MenuItem>
              <MenuItem value="color">اللون</MenuItem>
              <MenuItem value="size">الحجم</MenuItem>
              <MenuItem value="unit">المقاس</MenuItem>
              <MenuItem value="color_unit">لون / مقاس</MenuItem>
              <MenuItem value="color_size">لون / حجم</MenuItem>
              <MenuItem value="unit_size">حجم / مقاس</MenuItem>
            </Select>
          </FormControl>

          {featureGroups.map((group, arrayIndex) => (
            <div key={group.id}>
              <FeatureGroup
                index={group.id}
                arrayIndex={arrayIndex}
                isMobile={isMobile}
                isFirst={arrayIndex === 0}
                onDelete={handleDeleteGroup}
                onFeatureDataChange={handleFeatureDataChange}
                totalGroups={featureGroups.length}
                featureType={globalFeatureType}
                onLoadingChange={handleLoadingChange}
                controlGroupHasDiscount={controlGroupHasDiscount}
                onHasDiscountChange={handleGroupDiscountChange}
              />
              {arrayIndex < featureGroups.length - 1 && (
                <div
                  style={{
                    height: "2px",
                    background: "#E8E8E8",
                    margin: "20px 0",
                    width: "100%",
                  }}
                />
              )}
            </div>
          ))}

          <div className="add-more-container" onClick={handleAddGroup}>
            اضافة المزيد
          </div>
        </>
      )}
    </div>
  );
};

// FeatureGroup Component
const FeatureGroup = ({
  isMobile,
  isFirst,
  onDelete,
  index,
  arrayIndex,
  onFeatureDataChange,
  featureType,
  onLoadingChange,
  controlGroupHasDiscount,
  onHasDiscountChange,
}) => {
  // State for newly uploaded images
  const [images, setImages] = useState([]);
  const [hasDiscount, setHasDiscount] = useState(false);
  const [colorOptions, setColorOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [defaultPrice, setDefaultPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [discount, setDiscount] = useState("");
  const [maxDiscount, setMaxDiscount] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Fetch variation options whenever the featureType prop changes
  useEffect(() => {
    const fetchVariations = async (type) => {
      if (!type) return;

      setLoading(true);
      if (onLoadingChange) onLoadingChange(true);
      try {
        const response = await api.get(
          `/variations?page=1&per_page=10&type=${type}`,
        );
        if (response.data && response.data.data) {
          switch (type) {
            case "color":
              setColorOptions(response.data.data);
              break;
            case "size":
              setSizeOptions(response.data.data);
              break;
            case "unit":
              setUnitOptions(response.data.data);
              break;
            default:
              if (type.includes("color")) {
                const colorResponse = await api.get(
                  `/variations?page=1&per_page=10&type=color`,
                );
                if (colorResponse.data && colorResponse.data.data) {
                  setColorOptions(colorResponse.data.data);
                }
              }
              if (type.includes("size")) {
                const sizeResponse = await api.get(
                  `/variations?page=1&per_page=10&type=size`,
                );
                if (sizeResponse.data && sizeResponse.data.data) {
                  setSizeOptions(sizeResponse.data.data);
                }
              }
              if (type.includes("unit")) {
                const unitResponse = await api.get(
                  `/variations?page=1&per_page=10&type=unit`,
                );
                if (unitResponse.data && unitResponse.data.data) {
                  setUnitOptions(unitResponse.data.data);
                }
              }
              break;
          }
        }
      } catch (error) {
        console.error("Error fetching variations:", error);
      } finally {
        setLoading(false);
        if (onLoadingChange) onLoadingChange(false);
      }
    };

    fetchVariations(featureType);
  }, [featureType, onLoadingChange]);

  // Reset selections when featureType changes (skip the initial empty → first value transition)
  const prevFeatureTypeRef = useRef("");
  useEffect(() => {
    if (
      prevFeatureTypeRef.current &&
      prevFeatureTypeRef.current !== featureType
    ) {
      setSelectedColor("");
      setSelectedSize("");
      setSelectedUnit("");
    }
    prevFeatureTypeRef.current = featureType;
  }, [featureType]);

  // Image handling functions
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      }),
    );
    setImages((prevImages) => [...prevImages, ...newImages]);
  };

  const handleDeleteImage = (indexToDelete) => {
    // Revoke the URL to prevent memory leaks
    URL.revokeObjectURL(images[indexToDelete].preview);
    setImages(images.filter((_, i) => i !== indexToDelete));
  };

  const handleDelete = () => {
    onDelete(index);
  };

  // Keep a ref to images so the unmount cleanup always sees the latest list
  const imagesRef = useRef(images);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // Clean up URLs when component unmounts
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (onHasDiscountChange) onHasDiscountChange(index, hasDiscount);
  }, [hasDiscount, index, onHasDiscountChange]);

  // Prepare and send feature data to parent component
  useEffect(() => {
    if (onFeatureDataChange) {
      // Prepare and send feature data to parent component
      const featureData = new FormData();

      // Variation type
      featureData.append(`variation_type`, featureType);

      // Size, Color, Unit IDs based on feature type
      if (featureType.includes("size")) {
        featureData.append(`size_id`, selectedSize);
      }
      if (featureType.includes("color")) {
        featureData.append(`color_id`, selectedColor);
      }
      if (featureType.includes("unit")) {
        featureData.append(`unit_id`, selectedUnit);
      }

      // Variation details
      featureData.append(`variation_price`, defaultPrice);
      featureData.append(`variation_qty`, quantity);

      // Variation images
      images.forEach((image) => {
        featureData.append(`variation_images`, image);
      });

      // Discount information - always include these fields with empty values if not set
      featureData.append(`variation_discount`, hasDiscount ? discount : "0");
      featureData.append(
        `variation_max_discount`,
        hasDiscount ? maxDiscount : "0",
      );
      featureData.append(
        `variation_discount_date_from`,
        hasDiscount && startDate ? startDate.format("YYYY-MM-DD") : "",
      );
      featureData.append(
        `variation_discount_date_to`,
        hasDiscount && endDate ? endDate.format("YYYY-MM-DD") : "",
      );

      // Pass the form data, group ID, and array index to parent
      onFeatureDataChange(featureData, index, arrayIndex);
    }
  }, [
    featureType,
    selectedColor,
    selectedSize,
    selectedUnit,
    defaultPrice,
    quantity,
    images,
    hasDiscount,
    discount,
    maxDiscount,
    startDate,
    endDate,
    index,
    arrayIndex,
    onFeatureDataChange,
  ]);

  return (
    <div
      className="variations-group"
      style={{
        marginBottom: "20px",
        borderBottom: !isFirst ? "1px solid #eee" : "none",
        paddingBottom: "20px",
      }}
    >
      <div className="type-container">
        {/* Size Selection - Only show if feature type contains "size" */}
        {featureType.includes("size") && (
          <FormControl sx={{ width: isMobile && "100%", flex: 1 }}>
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              اختر الحجم
            </InputLabel>
            <Select
              labelId={`unit-select-${index}`}
              id={`size-select-${index}`}
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
              disabled={loading}
            >
              <MenuItem value="">
                {loading ? "جاري التحميل ..." : "الجميع"}
              </MenuItem>
              {sizeOptions.map((size) => (
                <MenuItem key={size.id} value={size.id}>
                  {size.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {/* Color Selection - Only show if feature type contains "color" */}
        {featureType.includes("color") && (
          <FormControl sx={{ width: isMobile && "100%", flex: 1 }}>
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              اختر اللون
            </InputLabel>
            <Select
              labelId={`color-select-${index}`}
              id={`color-select-${index}`}
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={selectedColor}
              onChange={(e) => setSelectedColor(e.target.value)}
              disabled={loading}
            >
              <MenuItem value="">
                {loading ? "جاري التحميل ..." : "الجميع"}
              </MenuItem>
              {colorOptions.map((color) => (
                <MenuItem key={color.id} value={color.id}>
                  {color.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {/* Unit Selection - Only show if feature type contains "unit" */}
        {featureType.includes("unit") && (
          <FormControl sx={{ width: isMobile && "100%", flex: 1 }}>
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              اختر المقاس
            </InputLabel>
            <Select
              labelId={`unit-select-${index}`}
              id={`unit-select-${index}`}
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              disabled={loading}
              defaultValue=""
            >
              <MenuItem value="">
                {loading ? "جاري التحميل ..." : "الجميع"}
              </MenuItem>
              {unitOptions.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl
          sx={{ width: isMobile && "100%", flex: 1 }}
          variant="outlined"
        >
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.8)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            السعر الافتراضي
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id={`default-price-${index}`}
            type="text"
            notched
            value={defaultPrice}
            onChange={(e) => setDefaultPrice(e.target.value)}
          />
        </FormControl>

        <FormControl
          sx={{ width: isMobile && "100%", flex: 1 }}
          variant="outlined"
        >
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.8)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الكمية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id={`quantity-${index}`}
            type="text"
            notched
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </FormControl>
      </div>

      <div className="product-form-img">
        {/* Display newly uploaded images */}
        <div
          className="imgs-container"
          style={{ display: images.length === 0 && "none" }}
        >
          {images.map((image, imgIndex) => (
            <div key={imgIndex} className="image-wrapper">
              <img src={image.preview} alt="" />
              <button
                className="delete-btn"
                onClick={() => handleDeleteImage(imgIndex)}
                type="button"
              >
                <DeleteIcon />
              </button>
            </div>
          ))}
        </div>
        <label htmlFor={`feature-images-${index}`} className="img-control">
          <img src={AddPhoto} alt="" />
          <span>اضافة صور</span>
        </label>
        <input
          type="file"
          id={`feature-images-${index}`}
          accept="image/*"
          multiple
          onChange={handleImageChange}
        />
      </div>

      {!controlGroupHasDiscount && (
        <div className="item">
          <input
            type="checkbox"
            checked={hasDiscount}
            onChange={(e) => setHasDiscount(e.target.checked)}
          />
          <label>يوجد خصم علي السمة</label>
        </div>
      )}

      {hasDiscount && !controlGroupHasDiscount && (
        <>
          <FormControl
            sx={{ width: isMobile ? "100%" : "316px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.8)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              الخصم
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id={`discount-${index}`}
              type="text"
              notched
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              endAdornment={<span style={{ marginLeft: "8px" }}>%</span>}
            />
          </FormControl>

          <FormControl
            sx={{ width: isMobile ? "100%" : "316px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.8)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              الحد الاقصي لقيمة الخصم
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id={`max-discount-${index}`}
              type="text"
              notched
              value={maxDiscount}
              onChange={(e) => setMaxDiscount(e.target.value)}
            />
          </FormControl>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "316px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: "row",
              borderRadius: "8px",
            }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              التاريخ من
            </InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer
                components={["DatePicker"]}
                sx={{
                  p: 0,
                  width: "316px",
                  borderRadius: "8px",
                }}
              >
                <DatePicker
                  sx={{ width: "100%" }}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="YYYY-MM-DD"
                />
              </DemoContainer>
            </LocalizationProvider>
          </FormControl>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "316px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: "row",
            }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              التاريخ الي
            </InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer
                components={["DatePicker"]}
                sx={{
                  p: 0,
                  width: "316px",
                }}
              >
                <DatePicker
                  sx={{ width: "100%" }}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="YYYY-MM-DD"
                />
              </DemoContainer>
            </LocalizationProvider>
          </FormControl>
        </>
      )}

      {!isFirst && (
        <div
          className="variations-footer"
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <img
            src={Delete}
            alt="حذف"
            onClick={handleDelete}
            style={{ cursor: "pointer" }}
          />
        </div>
      )}
    </div>
  );
};

export default Variations;
